import { useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { Input } from '~/components/ui/input';
import { familyLabel } from '~/utils/familyLabel';
import { Label } from '~/components/ui/label';
import { Button } from '~/components/ui/button';
import { Send } from 'lucide-react';
import { useEnrollmentInvitePreview } from '../Enrollments/useEnrollmentInvitePreview';
import { useInviteToEnroll } from '../Enrollments/useInviteToEnroll';
import { inviteOutcomeLabel, inviteResultLabel } from '../Enrollments/inviteWords';

interface InviteToEnrollDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schoolId: string;
  familyEnrollmentId: string;
  familyName: string;
  yearName: string;
  onSuccess?: () => void;
}

interface Outcome {
  status: string;
  reason?: string | null;
}

// The office invites one family to enroll. The preview says what each guardian
// will get; the server's answer decides what happened, and a refusal or error
// stays on screen with the dialog open.
export function InviteToEnrollDialog({
  open,
  onOpenChange,
  schoolId,
  familyEnrollmentId,
  familyName,
  yearName,
  onSuccess,
}: InviteToEnrollDialogProps) {
  const [deadlineOn, setDeadlineOn] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const sending = useRef(false);
  const { data, loading: previewLoading, error: previewError } = useEnrollmentInvitePreview(
    open ? schoolId : undefined,
    [familyEnrollmentId]
  );
  const [inviteToEnroll, { loading }] = useInviteToEnroll();

  const row = data?.enrollmentInvitePreview?.find((r) => r.familyEnrollmentId === familyEnrollmentId);
  const canInvite = row?.canInvite === true;

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setDeadlineOn('');
      setErrors([]);
      setOutcome(null);
    }
    onOpenChange(next);
  };

  const handleSend = async () => {
    if (sending.current) return;
    sending.current = true;
    setErrors([]);
    try {
      const result = await inviteToEnroll({
        variables: { input: { familyEnrollmentIds: [familyEnrollmentId], deadlineOn: deadlineOn || null } },
      });
      const payload = result.data?.inviteToEnroll;
      if (payload?.errors && payload.errors.length > 0) {
        setErrors(payload.errors);
        return;
      }
      const mine = payload?.results?.find((r) => r.familyEnrollmentId === familyEnrollmentId);
      if (!mine) {
        setErrors(['The invitation was not sent. Please try again.']);
        return;
      }
      setOutcome({ status: mine.status, reason: mine.reason });
      if (mine.status !== 'REFUSED') onSuccess?.();
    } catch (error) {
      setErrors([error instanceof Error ? error.message : 'The invitation was not sent. Please try again.']);
    } finally {
      sending.current = false;
    }
  };

  const sent = outcome !== null && outcome.status !== 'REFUSED';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Invite to enroll</DialogTitle>
          <DialogDescription>
            Invite the {familyLabel(familyName)} to enroll for {yearName}.
          </DialogDescription>
        </DialogHeader>

        {errors.length > 0 && (
          <div role="alert" className="space-y-1 text-sm text-destructive">
            {errors.map((message) => (
              <p key={message}>{message}</p>
            ))}
          </div>
        )}

        {outcome && (
          <p role="status" className={outcome.status === 'REFUSED' ? 'text-sm text-destructive' : 'text-sm'}>
            {inviteResultLabel(outcome.status)}
            {outcome.reason ? ` — ${outcome.reason}` : ''}
          </p>
        )}

        {previewLoading && <p className="text-sm text-muted-foreground">Checking who will be emailed…</p>}
        {previewError && <p role="alert" className="text-sm text-destructive">{previewError.message}</p>}
        {row && !row.canInvite && row.reason && (
          <p role="alert" className="text-sm text-destructive">{row.reason}</p>
        )}

        {row && row.warnings.length > 0 && (
          <div className="space-y-1 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {row.warnings.map((warning) => (
              <p key={warning}>{warning}</p>
            ))}
          </div>
        )}

        {row && (
          <ul className="divide-y rounded-md border">
            {row.guardians.map((guardian, index) => {
              const note = inviteOutcomeLabel(guardian.outcome);
              return (
              <li key={`${index}-${guardian.name}`} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-3 py-2 text-sm">
                <span className="min-w-0 break-words">
                  {guardian.name}
                  {guardian.email && <span className="ml-2 text-muted-foreground">{guardian.email}</span>}
                </span>
                {note && <span className="text-muted-foreground">{note}</span>}
              </li>
              );
            })}
          </ul>
        )}

        {!sent && (
          <div className="space-y-1">
            <Label htmlFor="invite-deadline">Deadline (optional)</Label>
            <Input id="invite-deadline" type="date" value={deadlineOn} onChange={(e) => setDeadlineOn(e.target.value)} />
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
            {sent ? 'Done' : 'Cancel'}
          </Button>
          {!sent && (
            <Button type="button" onClick={handleSend} disabled={loading || previewLoading || !canInvite}>
              {loading ? 'Sending…' : 'Send'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The office can invite a family year when the school's switch is on and the
 * year is not invited yet or was declined, whichever way its access was set,
 * or is halfway through the old flow (inviting moves it to the checklist). A
 * year enrolling on the checklist (resend instead) and an enrolled one don't.
 */
export function canInviteToEnroll(
  checklistEnrollmentOn: boolean | undefined,
  access: string | null | undefined,
  accessSource?: string | null,
): boolean {
  if (checklistEnrollmentOn !== true) return false;
  return access === 'NOT_INVITED' || access === 'DECLINED' || (access === 'ENROLLING' && accessSource === 'LEGACY');
}

interface InviteToEnrollActionProps extends Omit<InviteToEnrollDialogProps, 'open' | 'onOpenChange'> {
  checklistEnrollmentOn?: boolean;
  access?: string | null;
  accessSource?: string | null;
}

/** The "Invite to enroll" button (shown only where the rule allows) and its dialog. */
export function InviteToEnrollAction({ checklistEnrollmentOn, access, accessSource, ...dialogProps }: InviteToEnrollActionProps) {
  const [open, setOpen] = useState(false);
  // Only the button follows the rule. A successful send refetches and moves the
  // year to ENROLLING; the dialog stays mounted so its result and Done remain.
  return (
    <>
      {canInviteToEnroll(checklistEnrollmentOn, access, accessSource) && (
        <Button variant="outline" onClick={() => setOpen(true)}>
          <Send className="h-4 w-4 mr-2" />
          Invite to enroll
        </Button>
      )}
      <InviteToEnrollDialog {...dialogProps} open={open} onOpenChange={setOpen} />
    </>
  );
}

export default InviteToEnrollDialog;
